import applyAttribution from './attribution';

/**
 * Talent Triage cookie consent.
 *
 * Vendor IDs below are placeholders. Leave them empty until the production
 * values have been reviewed. An empty ID, or an empty ActiveCampaign script
 * URL, never creates a script element and never requests that vendor.
 *
 * Google Analytics is not loaded here. GA4 is expected to run from Google
 * Tag Manager. gtm.js is requested only after Analytics or Marketing is
 * granted. An empty container ID never requests gtm.js. The container must require
 * analytics_storage for GA4 and the advertising storage types for marketing
 * tags. This page only grants those types after an explicit choice.
 *
 * Filling an ID later does not change consent version 1, so a saved choice
 * still applies.
 *
 * Withdrawing a category expires the first-party cookie names listed here.
 * Cookies set on Google, LinkedIn, Meta or ActiveCampaign domains cannot
 * be removed by this site.
 */


const consentConfig = {
  cookieName: 'tt_consent',
  version: 1,
  maxAgeDays: 180,
  gtmContainerId: 'GTM-KRZTRM7D',
  categories: {
    analytics: [
      {
        id: 'google-analytics',
        cookies: ['_ga', '_gid', '_gat'],
        cookiePrefixes: ['_ga_']
      },
      {
        id: 'active-campaign',
        accountId: '',
        src: '',
        cookies: [],
        cookiePrefixes: []
      }
    ],
    marketing: [
      {
        id: 'linkedin',
        partnerId: '',
        src: 'https://snap.licdn.com/li.lms-analytics/insight.min.js',
        cookies: ['li_fat_id'],
        cookiePrefixes: []
      },
      {
        id: 'meta',
        pixelId: '',
        src: 'https://connect.facebook.net/en_US/fbevents.js',
        cookies: ['_fbp', '_fbc'],
        cookiePrefixes: []
      }
    ]
  }
};

let bannerEl = null;
let dialogEl = null;
let analyticsInput = null;
let marketingInput = null;
let returnFocusEl = null;

const injectedVendors = new Set();

function hasValue(value) {
  return typeof value === 'string' && value.trim() !== '';
}

function readRawCookie(name) {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
  return match ? match[1] : '';
}

function readConsent() {
  const raw = readRawCookie(consentConfig.cookieName);
  if (!raw) return null;

  let data = null;
  try {
    data = JSON.parse(decodeURIComponent(raw));
  } catch (error) {
    data = null;
  }

  if (!data || data.v !== consentConfig.version) return null;
  if (typeof data.analytics !== 'boolean') return null;
  if (typeof data.marketing !== 'boolean') return null;
  return data;
}

function writeConsent(choice) {
  const payload = {
    v: consentConfig.version,
    analytics: choice.analytics,
    marketing: choice.marketing,
    t: new Date().toISOString()
  };
  const maxAge = consentConfig.maxAgeDays * 24 * 60 * 60;
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  const value = encodeURIComponent(JSON.stringify(payload));
  document.cookie = `${consentConfig.cookieName}=${value}; Path=/; Max-Age=${maxAge}; SameSite=Lax${secure}`;
  return payload;
}

function domainVariants() {
  const host = window.location.hostname;
  const domains = [''];
  if (!host || host === 'localhost' || host.indexOf('.') === -1) return domains;
  if (/^[\d.]+$/.test(host)) return domains;
  domains.push(host);
  const parts = host.split('.');
  if (parts.length >= 2) domains.push(`.${parts.slice(-2).join('.')}`);
  return domains;
}

function expireCookie(name) {
  const expires = 'Thu, 01 Jan 1970 00:00:00 GMT';
  domainVariants().forEach((domain) => {
    const domainAttr = domain ? `; Domain=${domain}` : '';
    document.cookie = `${name}=; Path=/; Max-Age=0; Expires=${expires}${domainAttr}`;
  });
}

function cookieNames() {
  if (!document.cookie) return [];
  return document.cookie.split(';').map((part) => part.split('=')[0].trim()).filter(Boolean);
}

function clearVendor(vendor) {
  cookieNames().forEach((name) => {
    const listed = vendor.cookies.includes(name);
    const prefixed = vendor.cookiePrefixes.some((prefix) => name.indexOf(prefix) === 0);
    if (listed || prefixed) expireCookie(name);
  });
}

function clearCategory(category) {
  (consentConfig.categories[category] || []).forEach(clearVendor);
}

function appendScript(src, vendorId) {
  const script = document.createElement('script');
  script.async = true;
  script.src = src;
  script.setAttribute('data-tt-vendor', vendorId);
  document.head.appendChild(script);
}

function gtag() {
  // GTM recognises the arguments object, not a plain array.
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
}

function consentState(choice) {
  const analytics = choice && choice.analytics ? 'granted' : 'denied';
  const marketing = choice && choice.marketing ? 'granted' : 'denied';
  return {
    analytics_storage: analytics,
    ad_storage: marketing,
    ad_user_data: marketing,
    ad_personalization: marketing
  };
}

function pushConsent(command, choice) {
  window.dataLayer = window.dataLayer || [];
  gtag('consent', command, consentState(choice));
}

function loadGoogleTagManager(choice) {
  const granted = Boolean(choice && (choice.analytics || choice.marketing));
  if (!granted || !hasValue(consentConfig.gtmContainerId) || injectedVendors.has('gtm')) return;
  injectedVendors.add('gtm');
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js'
  });
  const containerId = encodeURIComponent(consentConfig.gtmContainerId.trim());
  appendScript(`https://www.googletagmanager.com/gtm.js?id=${containerId}`, 'gtm');
}

function injectActiveCampaign(vendor) {
  if (!hasValue(vendor.accountId) || !hasValue(vendor.src)) return;
  if (injectedVendors.has(vendor.id)) return;
  injectedVendors.add(vendor.id);
  window.visitorGlobalObjectAlias = 'vgo';
  window.vgo = window.vgo || function vgo() {
    window.vgo.q = window.vgo.q || [];
    // Vendor snippet queues an arguments object for the diffuser.
    // eslint-disable-next-line prefer-rest-params
    window.vgo.q.push(arguments);
  };
  window.vgo.l = new Date().getTime();
  appendScript(vendor.src, vendor.id);
  window.vgo('setAccount', vendor.accountId);
  window.vgo('setTrackByDefault', true);
  window.vgo('process');
}

function injectLinkedIn(vendor) {
  if (!hasValue(vendor.partnerId) || injectedVendors.has(vendor.id)) return;
  injectedVendors.add(vendor.id);
  const partnerKey = '_linkedin_partner_id';
  const partnersKey = '_linkedin_data_partner_ids';
  window[partnerKey] = vendor.partnerId;
  window[partnersKey] = window[partnersKey] || [];
  window[partnersKey].push(vendor.partnerId);
  if (!window.lintrk) {
    window.lintrk = function lintrk(action, data) {
      window.lintrk.q.push([action, data]);
    };
    window.lintrk.q = [];
  }
  appendScript(vendor.src, vendor.id);
}

function injectMeta(vendor) {
  if (!hasValue(vendor.pixelId) || injectedVendors.has(vendor.id)) return;
  injectedVendors.add(vendor.id);
  if (!window.fbq) {
    const fbq = function pixel() {
      if (fbq.callMethod) {
        // Vendor snippet forwards the arguments object into fbevents.js.
        // eslint-disable-next-line prefer-rest-params, prefer-spread
        fbq.callMethod.apply(fbq, arguments);
      } else {
        // eslint-disable-next-line prefer-rest-params
        fbq.queue.push(arguments);
      }
    };
    window.fbq = fbq;
    const fbqKey = '_fbq';
    if (!window[fbqKey]) window[fbqKey] = fbq;
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.queue = [];
    appendScript(vendor.src, vendor.id);
  }
  window.fbq('init', vendor.pixelId);
  window.fbq('track', 'PageView');
}

const injectors = {
  'active-campaign': injectActiveCampaign,
  linkedin: injectLinkedIn,
  meta: injectMeta
};

function injectCategory(category) {
  (consentConfig.categories[category] || []).forEach((vendor) => {
    const inject = injectors[vendor.id];
    if (inject) inject(vendor);
  });
}

function applySavedCategories(choice) {
  if (choice.analytics) injectCategory('analytics');
  if (choice.marketing) injectCategory('marketing');
}

function showBanner() {
  if (bannerEl) bannerEl.hidden = false;
}

function hideBanner() {
  if (bannerEl) bannerEl.hidden = true;
}

function syncSwitch(input) {
  if (!input) return;
  input.setAttribute('aria-checked', input.checked ? 'true' : 'false');
}

function releaseBannerFocus() {
  const main = document.querySelector('main');
  if (!(main instanceof HTMLElement)) return;
  if (!main.hasAttribute('tabindex')) main.setAttribute('tabindex', '-1');
  main.focus();
}

function restoreFocus() {
  const target = returnFocusEl;
  returnFocusEl = null;
  if (target && document.contains(target) && !target.closest('[hidden]')) {
    target.focus();
    return;
  }
  releaseBannerFocus();
}

function openPreferences(trigger) {
  if (!dialogEl || typeof dialogEl.showModal !== 'function' || dialogEl.open) return;
  returnFocusEl = trigger || null;
  const saved = readConsent();
  if (analyticsInput) analyticsInput.checked = saved ? saved.analytics : false;
  if (marketingInput) marketingInput.checked = saved ? saved.marketing : false;
  syncSwitch(analyticsInput);
  syncSwitch(marketingInput);
  hideBanner();
  dialogEl.showModal();
  const closeButton = dialogEl.querySelector('[data-tt-consent="close"]');
  if (closeButton instanceof HTMLElement) closeButton.focus();
}

function commitChoice(next) {
  const previous = readConsent();
  const saved = writeConsent(next);
  hideBanner();
  const revokedAnalytics = Boolean(previous && previous.analytics && !saved.analytics);
  const revokedMarketing = Boolean(previous && previous.marketing && !saved.marketing);
  if (revokedAnalytics) clearCategory('analytics');
  if (revokedMarketing) clearCategory('marketing');
  if (!saved.analytics) applyAttribution(false);
  pushConsent('update', saved);
  const dialogWasOpen = Boolean(dialogEl && dialogEl.open);
  if (dialogWasOpen) dialogEl.close();
  if (revokedAnalytics || revokedMarketing) {
    window.location.reload();
    return;
  }
  if (saved.analytics) applyAttribution(true);
  loadGoogleTagManager(saved);
  applySavedCategories(saved);
  if (!dialogWasOpen) releaseBannerFocus();
}

function onBannerClick(event) {
  const action = event.target.closest('[data-tt-consent]');
  if (!action) return;
  const kind = action.getAttribute('data-tt-consent');
  if (kind === 'accept') commitChoice({ analytics: true, marketing: true });
  if (kind === 'reject') commitChoice({ analytics: false, marketing: false });
  if (kind === 'manage') openPreferences(action);
}

function onDocumentClick(event) {
  const opener = event.target.closest('[data-tt-consent-open]');
  if (!opener) return;
  event.preventDefault();
  openPreferences(opener);
}

function onDialogClick(event) {
  const closer = event.target.closest('[data-tt-consent="close"]');
  if (closer && dialogEl) dialogEl.close();
}

function onDialogKeydown(event) {
  if (event.key !== 'Escape' || !dialogEl || !dialogEl.open) return;
  event.preventDefault();
  dialogEl.close();
}

function onDialogClose() {
  if (!readConsent()) showBanner();
  restoreFocus();
}

function onPreferencesSubmit(event) {
  event.preventDefault();
  commitChoice({
    analytics: Boolean(analyticsInput && analyticsInput.checked),
    marketing: Boolean(marketingInput && marketingInput.checked)
  });
}

const consentInit = () => {
  const saved = readConsent();
  pushConsent('default', saved);
  applyAttribution(Boolean(saved && saved.analytics));

  bannerEl = document.getElementById('tt-consent-banner');
  dialogEl = document.getElementById('tt-consent-dialog');
  if (!bannerEl || !dialogEl) return;

  analyticsInput = document.getElementById('tt-consent-analytics');
  marketingInput = document.getElementById('tt-consent-marketing');

  if (saved) {
    hideBanner();
    loadGoogleTagManager(saved);
    applySavedCategories(saved);
  } else {
    showBanner();
  }

  bannerEl.addEventListener('click', onBannerClick);
  document.addEventListener('click', onDocumentClick);
  dialogEl.addEventListener('click', onDialogClick);
  dialogEl.addEventListener('keydown', onDialogKeydown);
  dialogEl.addEventListener('close', onDialogClose);

  const form = dialogEl.querySelector('[data-tt-consent="form"]');
  if (form) form.addEventListener('submit', onPreferencesSubmit);

  [analyticsInput, marketingInput].forEach((input) => {
    if (!input) return;
    input.addEventListener('change', () => syncSwitch(input));
  });
};

export default consentInit;
